import { TOMIS_API_URL } from "@/constants";

/** Django Standardized API return format  */
export interface StandardizedApi<Data> {
  success: boolean;
  data: Data;
  error?: {
    /** A human-readable error message */
    message: string;
    /** A_STABLE_ERROR_CODE */
    code: string;
    /** An array of strings for more technical error info */
    details: Array<string>;
  };
}

interface RequestOptions {
  method: "GET" | "POST" | "PATCH";
  endpoint: string;
  body?: any;
}

async function request<T>(
  options: RequestOptions,
): Promise<{ data?: T; error?: string }> {
  const { method, endpoint, body } = options;
  const url = `${TOMIS_API_URL}${endpoint}`;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  const config: RequestInit = {
    method,
    headers,
  };

  if (body && (method === "POST" || method === "PATCH")) {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, config);

    // Try to parse JSON response
    let jsonData: StandardizedApi<T>;
    try {
      jsonData = (await response.json()) as StandardizedApi<T>;
    } catch (parseError) {
      // If JSON parsing fails (e.g., 500 error returning HTML), provide clear message
      if (!response.ok) {
        return {
          error: `Server error (${response.status}). Please try again in a moment.`,
        };
      }
      throw parseError;
    }

    const { data, success, error } = jsonData;

    if (!response.ok || !success) {
      throw new Error(
        error?.message || `Request failed (status: ${response.status})`,
      );
    }

    return { data };
  } catch (error) {
    console.error("API request failed:", error);

    // Handle network-level errors with user-friendly messages
    if (error instanceof TypeError) {
      // "Failed to fetch" indicates network issue (offline, server down, CORS)
      if (error.message.includes("Failed to fetch")) {
        return {
          error:
            "Unable to connect. Please check your internet connection and try again.",
        };
      }
    }

    // Return the error message if it's an Error object
    if (error instanceof Error) {
      return { error: error.message };
    }

    if (typeof error === "string") {
      return { error };
    }

    return { error: "Something went wrong. Please try again." };
  }
}

export const api = {
  async get<T>(endpoint: string) {
    return request<T>({ method: "GET", endpoint });
  },

  async post<T>(endpoint: string, body?: any) {
    return request<T>({ method: "POST", endpoint, body });
  },

  async patch<T>(endpoint: string, body?: any) {
    return request<T>({ method: "PATCH", endpoint, body });
  },
};
